import { Link } from 'react-router-dom';
import { 
  FileText, 
  BadgeCheck, 
  TrendingUp, 
  Search, 
  UserPlus, 
  Upload, 
  ShieldCheck, 
  Eye,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";
const HERO_BG = "https://images.unsplash.com/photo-1750582343233-880f73565bcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBtZWV0aW5nJTIwZGl2ZXJzZSUyMHByb2Zlc3Npb25hbHMlMjBzb3V0aCUyMGFmcmljYSUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzMyNDc0NTl8MA&ixlib=rb-4.1.0&q=85";
const STUDENT_PROFILE = "https://images.unsplash.com/photo-1765648684630-ac9c15ac98d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzMyNDc0NzB8MA&ixlib=rb-4.1.0&q=85";
const PROFESSIONAL_PROFILE = "https://images.unsplash.com/photo-1738750908048-14200459c3c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxhZnJpY2FuJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzMyNDc0NzB8MA&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: FileText,
    title: 'Dynamic CV',
    description: 'Your profile evolves automatically as you add new achievements, training, and experiences.',
    color: 'bg-meritum-teal',
  },
  {
    icon: BadgeCheck,
    title: 'Verified Achievements',
    description: 'All claims supported by documented evidence and referee validation for maximum credibility.',
    color: 'bg-meritum-gold',
  },
  {
    icon: TrendingUp,
    title: 'Track Development',
    description: 'Visualize your career progression with a comprehensive record of growth and accomplishments.',
    color: 'bg-meritum-teal',
  },
  {
    icon: Search,
    title: 'Skill Discovery',
    description: 'Get discovered by organizations seeking evidence-based skill identification.',
    color: 'bg-meritum-gold',
  },
];

const steps = [
  {
    icon: UserPlus,
    title: 'Create Profile',
    description: 'Sign up and build your professional profile with your academic and practical achievements.',
  },
  {
    icon: Upload,
    title: 'Add Portfolio Links',
    description: 'Link your GitHub, YouTube, Behance, LinkedIn, Google Drive, ORCID and other portfolios.',
  },
  {
    icon: ShieldCheck,
    title: 'Get Verified',
    description: 'Request referees to validate your accomplishments for trusted credibility.',
  },
  {
    icon: Eye,
    title: 'Get Discovered',
    description: 'Become visible to organizations looking for evidence-based skills.',
  },
];

const missedOpportunities = [
  'Skilled graduates overlooked because their achievements aren\'t visible to employers',
  'Organizations missing out on top candidates hidden behind generic CVs',
  'Valuable skills and certifications buried in static documents that nobody sees',
  'Career growth stalled because there\'s no clear way to showcase verified progress',
];

export default function Home() {
  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BG}
            alt="Professional team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-meritum-navy/95 via-meritum-teal/90 to-meritum-teal/70" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 pt-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-meritum-gold/20 border border-meritum-gold/40 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6 animate-fade-in">
                Skill Discovery Platform
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-6 animate-fade-in-up">
                Progress Made
                <span className="block text-meritum-gold">Visible</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed mb-8 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Build a verified professional profile that grows with your training and achievements. 
                Get discovered by organizations seeking evidence-based skills.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <Link to="/pricing">
                  <Button 
                    data-testid="hero-create-profile-btn"
                    className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
                  >
                    Create Profile
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/organizations">
                  <Button 
                    data-testid="hero-explore-skill-btn"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
                  >
                    Explore Skills
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column - Floating Profile Card */}
            <div className="hidden lg:block animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white/10 backdrop-blur-xl rounded-lg p-6 border border-white/20 shadow-2xl transform hover:scale-[1.02] transition-transform duration-300">
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={STUDENT_PROFILE}
                      alt="Profile"
                      className="w-16 h-16 rounded-full object-cover border-2 border-meritum-gold"
                    />
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">Nomalanga Khumalo</h3>
                      <p className="font-body text-sm text-slate-300">Software Engineer</p>
                    </div>
                    <span className="ml-auto px-3 py-1 bg-meritum-gold text-meritum-navy text-xs font-bold rounded-sm">
                      Verified
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-200">
                      <CheckCircle2 size={16} className="text-meritum-gold" />
                      <span className="font-body text-sm">BSc Computer Science - UCT</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-200">
                      <CheckCircle2 size={16} className="text-meritum-gold" />
                      <span className="font-body text-sm">AWS Certified Developer</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-200">
                      <CheckCircle2 size={16} className="text-meritum-gold" />
                      <span className="font-body text-sm">GitHub Portfolio Linked</span>
                    </div>
                  </div>
                </div>
                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-meritum-gold/20 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-meritum-teal/20 rounded-full blur-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section data-testid="why-this-matters-section" className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 font-body text-sm font-medium rounded-sm mb-4">
                Why This Matters
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
                Missed Opportunities Are Everywhere
              </h2>
              <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed mb-6">
                Every day, qualified professionals and promising graduates miss out on opportunities 
                simply because their skills remain invisible. Here's what's at stake:
              </p>
              <ul className="space-y-4">
                {missedOpportunities.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative lg:pl-8">
              <img
                src={PROFESSIONAL_PROFILE}
                alt="Professional looking for opportunities"
                className="rounded-lg shadow-xl w-full h-auto max-h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section data-testid="solution-section" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="bg-meritum-teal/5 rounded-lg p-8 border border-meritum-teal/10">
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
                      <BadgeCheck className="text-meritum-gold" size={20} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium text-meritum-navy">Achievement Verified</p>
                      <p className="font-body text-xs text-slate-500">Project Management Certificate</p>
                    </div>
                    <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      Confirmed
                    </span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-10 h-10 bg-meritum-teal/20 rounded-full flex items-center justify-center">
                      <TrendingUp className="text-meritum-teal" size={20} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium text-meritum-navy">Portfolio Linked</p>
                      <p className="font-body text-xs text-slate-500">GitHub, Behance, LinkedIn</p>
                    </div>
                    <span className="ml-auto px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
                      <Eye className="text-meritum-gold" size={20} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium text-meritum-navy">Profile Viewed</p>
                      <p className="font-body text-xs text-slate-500">Tech Corp HR Department</p>
                    </div>
                    <span className="ml-auto px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                      Today
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-3 py-1 bg-meritum-teal/10 text-meritum-teal font-body text-sm font-medium rounded-sm mb-4">
                The Solution
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
                Evidence-Based Professional Profiles
              </h2>
              <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed mb-6">
                MeritumPraxis enables you to build a dynamic professional profile where achievements 
                are recorded, verified, and discoverable. Link your portfolios from GitHub, YouTube, 
                Behance, LinkedIn, Google Drive, ORCID and more.
              </p>
              <ul className="space-y-4">
                {[
                  'Documented achievements with evidence',
                  'Referee validation for credibility',
                  'Portfolio links (GitHub, Behance, LinkedIn, ORCID)',
                  'Discoverable by leading organizations',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section data-testid="benefits-section" className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
              Key Benefits
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              Why Choose MeritumPraxis?
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Our platform offers unique advantages for both candidates and organizations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`benefit-${benefit.title.toLowerCase().replace(' ', '-')}`}
                className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className={`w-14 h-14 ${benefit.color} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <benefit.icon className="text-white" size={24} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {benefit.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Verification Works Section */}
      <section data-testid="verification-section" className="py-20 md:py-28 bg-meritum-navy/5">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 font-body text-sm font-medium rounded-sm mb-4">
              Trust & Verification
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              Verified Skills, Trusted Profiles
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Our reference verification system ensures every skill claim is backed by real people. Organizations can trust what they see.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Send className="text-blue-600" size={28} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-2">1. Invite Your Referee</h3>
              <p className="font-body text-sm text-slate-600">Add a reference to your skill, then send them a one-click verification invite via email.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <ShieldCheck className="text-emerald-600" size={28} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-2">2. Referee Confirms</h3>
              <p className="font-body text-sm text-slate-600">Your referee receives a secure link and can confirm or decline with an optional comment.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 text-center">
              <div className="w-16 h-16 bg-meritum-gold/20 rounded-full flex items-center justify-center mx-auto mb-5">
                <BadgeCheck className="text-meritum-gold" size={28} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-2">3. Badge Displayed</h3>
              <p className="font-body text-sm text-slate-600">Verified skills get a trust badge visible to organizations, making your profile stand out.</p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/pricing">
              <Button className="bg-emerald-600 text-white hover:bg-emerald-700 font-body font-bold px-8 py-4" data-testid="verification-cta-btn">
                Start Building Your Verified Profile
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section data-testid="how-it-works-section" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-teal/10 text-meritum-teal font-body text-sm font-medium rounded-sm mb-4">
              How It Works
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              Four Simple Steps
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Get started in minutes and begin building your verified professional profile.
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-meritum-gold/30 -translate-y-1/2 z-0" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
              {steps.map((step, index) => (
                <div
                  key={index}
                  data-testid={`step-${index + 1}`}
                  className="text-center"
                >
                  <div className="relative inline-block mb-6">
                    <div className="w-20 h-20 bg-white border-4 border-meritum-gold rounded-full flex items-center justify-center shadow-lg">
                      <step.icon className="text-meritum-teal" size={32} />
                    </div>
                    <span className="absolute -top-2 -right-2 w-8 h-8 bg-meritum-gold text-meritum-navy font-bold text-sm rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 md:py-28 bg-meritum-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
            Ready to Make Your Progress Visible?
          </h2>
          <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Join thousands of professionals building verified profiles and connecting with 
            organizations that value evidence-based skills.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button 
                data-testid="cta-create-profile-btn"
                className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
              >
                Create Your Profile
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button 
                data-testid="cta-pricing-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
